"use client";

import { Box, Typography, keyframes, Stack } from "@mui/material";
import MessageItem from "../explore/messageItem";
import { ChartData } from "../chartRenderer";

import { useEffect, useMemo, useRef, useState } from "react";
import { useConversational } from "@/context/ConversationalContext";
import Image from "next/image";

// typing dots animation
const bounce = keyframes`
  0% { transform: translateY(0); opacity: 0.4; }
  50% { transform: translateY(-3px); opacity: 1; }
  100% { transform: translateY(0); opacity: 0.4; }
`;

type ChatMessage = {
  role: "user" | "assistant" | "human" | "ai";
  message: string;
  charts?: ChartData[] | any;
  createdAt?: string;
  id:string
};

const ChatContainer = ({ messages }: { messages: ChatMessage[] }) => {
  const { isLoading, isStreaming, streamingMsg } = useConversational();

  const containerRef = useRef<HTMLDivElement | null>(null);

  /* --------------------------- Thinking Text Loop --------------------------- */
  const thinkingTexts = useMemo(
    () => [
      "Thinking…",
      "Fetching insights…",
      "Generating data…",
      "Drafting response…",
    ],
    []
  );
  const [thinkingIndex, setThinkingIndex] = useState(0);

  useEffect(() => {
    if (!isLoading) return;
    setThinkingIndex(0);

    const id = setInterval(() => {
      setThinkingIndex((prev) => (prev + 1) % thinkingTexts.length);
    }, 1500);

    return () => clearInterval(id);
  }, [isLoading, thinkingTexts]);

  /* ----------------------------- Thinking Bubble ----------------------------- */
  // Styled to match a non-human (assistant) MessageItem
  const renderThinking = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        mb: 2,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          maxWidth: "100%",
          flex: 1,
        }}
      >
        <Box
          sx={{
            background: "rgba(255,255,255,0.1)", // same as assistant bubble
            border: "none",
            p: "12px 24px",
            borderRadius: "16px",
            color: "#fff",
            fontSize: 14,
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
          }}
        >
          {/* Same header as assistant in MessageItem */}
          <Stack
            sx={{ mt: "4px" }}
            direction="row"
            alignItems="center"
            gap={1}
            mb={1}
          >
            <Image
              src="https://sr-cdn-1.shiprocket.in/sr-promise/images/trends_ai_chat_icon.svg"
              alt="AI"
              width={24}
              height={24}
            />
            <Typography color="#8C9FFF" fontWeight={600} fontSize={14}>
              TRENDS AI
            </Typography>
          </Stack>

          {/* Thinking text + animated dots */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minWidth: 300,
            }}
          >
            <Typography variant="body2">
              {thinkingTexts[thinkingIndex]}
            </Typography>

            <Box sx={{ display: "flex", gap: 0.6 }}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "999px",
                    background: "#fff",
                    animation: `${bounce} 1s ease-in-out ${i * 0.15}s infinite`,
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  /* -------------------------- Merge Streaming Message -------------------------- */
  const mergedMessages = useMemo(() => {
    if (isStreaming && streamingMsg?.message) {
      const r = streamingMsg.role as string;
      const normalized: ChatMessage["role"] =
        r === "user" || r === "human" ? "user" : "assistant";

      return [
        ...messages,
        {
          ...streamingMsg,
          role: normalized,
        },
      ] as ChatMessage[];
    }

    return messages;
  }, [messages, isStreaming, streamingMsg]);

  /* ----------------------------- Auto scroll down ----------------------------- */
  // Kept exactly like your original: scroll when messages array changes
  useEffect(() => {
    if (isLoading) {
      const el = containerRef.current;
      if (!el) return;

      const id = window.requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
      return () => cancelAnimationFrame(id);
    }
  }, [isLoading]);

  /* ---------------------------------- UI ---------------------------------- */

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        overflowY: "auto",
        overflowX: "hidden",
        px: { xs: 2, sm: 3, md: 6 },
        py: { xs: 2, md: 3 },
        scrollBehavior: "smooth",
      }}
    >
      {/* Render messages (including streaming, via mergedMessages) */}
      {mergedMessages.map((msg, idx) => (
        <MessageItem
          key={idx}
          role={msg.role}
          message={msg.message}
          charts={msg.charts}
          time={msg.createdAt}
          messageId={msg.id}
          autoScroll={(scrollamt : number) =>{
            const el = containerRef.current;
      if (!el) return;

       window.requestAnimationFrame(() => {
        el.scrollTop = el.scrollTop + scrollamt;
      });
          }}
        />
      ))}

      {/* Thinking bubble in same visual format as assistant message */}
      {isLoading && !isStreaming && renderThinking()}
    </Box>
  );
};

export default ChatContainer;
